import {
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AsinSeed, AsinSeedTypeEnum, Marketplace, MatchType, Targeting } from "@front/m19-api-client";
import { AccountSelectionService, AsinService, Constant, SegmentService } from "@front/m19-services";
import {
  IAlertComponent,
  IButtonDirective,
  ITab,
  ITabsComponent,
  MODAL_DATA,
  ModalRef,
  ModalService,
  ToasterService,
  IIconComponent,
} from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { KeywordListManagerComponent } from "@m19-board/shared/keyword-list-manager/keyword-list-manager.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { SeedType } from "./add-to-seeds-modal.component";

export interface SeedsEditionModalData {
  accountId: string;
  marketplace: Marketplace;
  asin: string;
  keywordTargetingItems: AsinSeed[];
  productTargetingItems: AsinSeed[];
  whitelist: boolean;
}

@UntilDestroy()
@Component({
  selector: "app-seeds-edition-modal",
  templateUrl: "./seeds-edition-modal.component.html",
  imports: [
    TranslocoDirective,
    IButtonDirective,
    ITabsComponent,
    KeywordListManagerComponent,
    AsinSelectionComponent,
    IAlertComponent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeedsEditionModalComponent {
  private asinService = inject(AsinService);
  private toasterService = inject(ToasterService);
  private accountSelectionService = inject(AccountSelectionService);
  private translocoService = inject(TranslocoService);
  private modalService = inject(ModalService);

  readonly modalData = inject(MODAL_DATA) as SeedsEditionModalData;
  readonly modalRef = inject(ModalRef);
  readonly destroyRef = inject(DestroyRef);

  readonly SeedType = SeedType;
  readonly MAX_WORD_PER_KEYWORD = SegmentService.MATCH_TYPE_EXACT_MAX_WORDS;
  readonly MAX_KEYWORDS = Constant.maxKeywordSeeds;

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$);
  @ViewChild("addProductsModal") addProductsModal!: TemplateRef<any>;
  addProductsModalRef: ModalRef<string[]> | undefined;
  activeTab = signal<SeedType>(SeedType.Keyword);
  whitelistedKeywords = signal<AsinSeed[]>(
    this.modalData.keywordTargetingItems.filter(
      (x) => x.type == AsinSeedTypeEnum.WHITELIST && x.matchType !== MatchType.asinSameAs,
    ),
  );
  blacklistedKeywords = signal<AsinSeed[]>(
    this.modalData.keywordTargetingItems.filter(
      (x) => x.type == AsinSeedTypeEnum.BLACKLIST && x.matchType !== MatchType.asinSameAs,
    ),
  );
  initWhitelistedKeywords = computed(() => this.whitelistedKeywords().map((x) => this.toTargeting(x)));
  initBlacklistedKeywords = computed(() => this.blacklistedKeywords().map((x) => this.toTargeting(x)));
  whitelistedProducts = signal<AsinSeed[]>(
    this.modalData.productTargetingItems.filter(
      (x) => x.type == AsinSeedTypeEnum.WHITELIST && x.matchType === MatchType.asinSameAs,
    ),
  );
  blacklistedProducts = signal<AsinSeed[]>(
    this.modalData.productTargetingItems.filter(
      (x) => x.type == AsinSeedTypeEnum.BLACKLIST && x.matchType === MatchType.asinSameAs,
    ),
  );
  initWhitelistedProducts = computed(() => this.whitelistedProducts().map((x) => x.targetingValue!));
  initBlacklistedProducts = computed(() => this.blacklistedProducts().map((x) => x.targetingValue!));
  tabs = computed<ITab<SeedType>[]>(() => [
    {
      label: this.translocoService.translate("common.keyword"),
      value: SeedType.Keyword,
      badge: this.modalData.whitelist
        ? this.whitelistedKeywords().length.toString()
        : this.blacklistedKeywords().length.toString(),
    },
    {
      label: this.translocoService.translate("common.product"),
      value: SeedType.Product,
      badge: this.modalData.whitelist
        ? this.whitelistedProducts().length.toString()
        : this.blacklistedProducts().length.toString(),
    },
  ]);
  loading = signal(false);
  disabledAsins = computed(
    () => new Set(this.modalData.whitelist ? this.initWhitelistedProducts() : this.initBlacklistedProducts()),
  );
  productToAdd = signal<string[]>([]);
  productToRemove = signal<string[]>([]);

  keywordTargetingsChanged(targetings: Targeting[]) {
    if (this.modalData.whitelist) {
      this.whitelistedKeywords.set(
        targetings.map((x) => ({
          targetingValue: x.targetingValue!,
          type: AsinSeedTypeEnum.WHITELIST,
          matchType: MatchType.exact,
        })),
      );
    } else {
      this.blacklistedKeywords.set(
        targetings.map((x) => ({
          targetingValue: x.targetingValue!,
          type: AsinSeedTypeEnum.BLACKLIST,
          matchType: x.matchType,
        })),
      );
    }
  }

  productToAddChanged(asins: string[]) {
    this.productToAdd.set(asins);
  }

  openAddProductsModal() {
    this.addProductsModalRef = this.modalService.openModal(this.addProductsModal, {
      size: "lg",
      height: "fill",
    });
    this.addProductsModalRef.afterClosed().subscribe((_) => {
      this.productToAdd.set([]);
      this.productToRemove.set([]);
    });
  }

  saveProducts() {
    const toAdd: AsinSeed[] = this.productToAdd().map((x) => {
      const normalizedAsin = Utils.normalizeASIN(x);
      const seed: AsinSeed = {
        targetingValue: normalizedAsin,
        type: this.modalData.whitelist ? AsinSeedTypeEnum.WHITELIST : AsinSeedTypeEnum.BLACKLIST,
        matchType: MatchType.asinSameAs,
      };
      return seed;
    });

    if (this.modalData.whitelist) {
      this.whitelistedProducts.update((x) =>
        x.concat(toAdd).filter((x) => !this.productToRemove().includes(x.targetingValue!)),
      );
    } else {
      this.blacklistedProducts.update((x) =>
        x.concat(toAdd).filter((x) => !this.productToRemove().includes(x.targetingValue!)),
      );
    }

    this.productToAdd.set([]);
    this.productToRemove.set([]);
    this.save();
  }

  save() {
    this.loading.set(true);
    this.asinService
      .setAsinSeeds(
        this.modalData.accountId,
        this.modalData.marketplace,
        this.modalData.asin,
        this.whitelistedKeywords().concat(this.blacklistedKeywords()),
        this.whitelistedProducts().concat(this.blacklistedProducts()),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toasterService.success(
            `ASIN ${this.modalData.asin} seed keywords and products successfully updated`,
            "Seed keywords and products update",
          );
          if (this.activeTab() === SeedType.Keyword) {
            this.modalRef.close();
            return;
          }

          this.addProductsModalRef?.close();
          this.productToAdd.set([]);
          this.productToRemove.set([]);
          this.loading.set(false);
        },
        error: (errors: any) => {
          this.toasterService.error(JSON.stringify(errors), "Seed keywords and products update");
          this.loading.set(false);
        },
      });
  }

  close() {
    this.modalRef.close();
  }

  private toTargeting(seed: AsinSeed): Targeting {
    return {
      targetingValue: seed.targetingValue!,
      matchType: seed.matchType!,
    };
  }
}
